import React from 'react';
import { Link } from 'react-router-dom';
import { SITE_CONFIG } from '../config';
import { useLanguage } from '../contexts/LanguageContext';
import SEOHelmet from './SEOHelmet';
import PublicHeader from './PublicHeader';
import './GiovanniSite.css';

const content = {
  en: {
    kicker: 'HOUSEITGOING buyer benefit',
    title: 'A cash-back deal built around your actual purchase.',
    lead: 'On qualifying home purchases, HOUSEITGOING may return part of the brokerage compensation it receives to you as a buyer rebate or approved closing credit. The opportunity is evaluated around your property, financing, and transaction—not a one-size-fits-all headline.',
    badgeTop: 'Potential buyer benefit',
    badgeMain: 'Cash back',
    badgeBottom: 'Estimated clearly. Confirmed in writing.',
    howKicker: 'Simple and transparent',
    howTitle: 'How the cash-back deal works.',
    steps: [
      ['Tell us about your move', 'Share your target area, timing, financing plan, and the kind of home you want to purchase.'],
      ['Review your estimated benefit', 'Once the property and transaction structure are known, Giovanni explains whether the purchase qualifies and provides the estimated value in writing.'],
      ['Apply it through the closing process', 'If approved, the benefit is coordinated with your lender and closing team as a permitted credit or rebate.']
    ],
    qualifyKicker: 'What determines eligibility',
    qualifyTitle: 'The details of the transaction matter.',
    qualifyBody: 'Cash-back availability and value can change from one purchase to another. Giovanni will review the complete picture before presenting an estimate.',
    factors: ['Purchase price and property type', 'Brokerage compensation available in the transaction', 'Services required to represent you', 'Loan program and lender limits', 'Closing costs and closing-party approval', 'Applicable law and written agreement'],
    benefitKicker: 'Designed for real-life buying costs',
    benefitTitle: 'More flexibility for the move ahead.',
    benefits: [
      ['Closing-cost support', 'When permitted, an approved credit may help cover eligible costs due at closing.'],
      ['Post-closing rebate', 'If the transaction and applicable rules allow it, the benefit may be delivered after closing.'],
      ['Clear expectations', 'You receive the estimated amount and conditions in writing before relying on the benefit.']
    ],
    disclaimer: 'Cash-back availability and amount are not guaranteed. Any rebate or credit is subject to a written agreement, transaction economics, applicable law, lender rules, and approval by the closing parties. A credit cannot exceed permitted closing costs and may affect loan terms. This is not a loan offer, tax advice, or a promise of funds.',
    ctaTitle: 'Let’s estimate what your purchase may qualify for.',
    ctaBody: 'Tell Giovanni where and when you plan to buy. He will explain the offer alongside the rest of your home-buying strategy.',
    contact: 'Check my eligibility',
    search: 'Search Atlanta homes'
  },
  es: {
    kicker: 'Beneficio para compradores de HOUSEITGOING',
    title: 'Una oferta de dinero de vuelta basada en su compra real.',
    lead: 'En compras de vivienda que califican, HOUSEITGOING puede devolverle parte de la compensación que recibe la correduría como reembolso para el comprador o crédito aprobado para el cierre. La oportunidad se evalúa según la propiedad, el financiamiento y la transacción; no es una promesa general para todos.',
    badgeTop: 'Beneficio potencial para el comprador',
    badgeMain: 'Dinero de vuelta',
    badgeBottom: 'Estimado con claridad. Confirmado por escrito.',
    howKicker: 'Simple y transparente',
    howTitle: 'Cómo funciona la oferta.',
    steps: [
      ['Cuéntenos sobre su compra', 'Comparta el área, el plazo, su plan de financiamiento y el tipo de vivienda que desea comprar.'],
      ['Revise su beneficio estimado', 'Cuando se conozcan la propiedad y la estructura de la transacción, Giovanni le explicará si califica y presentará el valor estimado por escrito.'],
      ['Aplíquelo mediante el proceso de cierre', 'Si se aprueba, el beneficio se coordina con su prestamista y el equipo de cierre como crédito o reembolso permitido.']
    ],
    qualifyKicker: 'Qué determina la elegibilidad',
    qualifyTitle: 'Los detalles de la transacción importan.',
    qualifyBody: 'La disponibilidad y el valor pueden cambiar de una compra a otra. Giovanni revisará la situación completa antes de presentar un estimado.',
    factors: ['Precio de compra y tipo de propiedad', 'Compensación disponible para la correduría', 'Servicios necesarios para representarle', 'Programa de préstamo y límites del prestamista', 'Costos y aprobación del equipo de cierre', 'Leyes aplicables y acuerdo por escrito'],
    benefitKicker: 'Pensado para los costos reales de comprar',
    benefitTitle: 'Más flexibilidad para su próxima etapa.',
    benefits: [
      ['Apoyo con costos de cierre', 'Cuando se permita, un crédito aprobado puede ayudar a cubrir costos elegibles del cierre.'],
      ['Reembolso después del cierre', 'Si la transacción y las reglas aplicables lo permiten, el beneficio puede entregarse después del cierre.'],
      ['Expectativas claras', 'Recibirá por escrito el valor estimado y las condiciones antes de depender del beneficio.']
    ],
    disclaimer: 'La disponibilidad y el valor del beneficio no están garantizados. Todo reembolso o crédito está sujeto a un acuerdo por escrito, la economía de la transacción, las leyes aplicables, las reglas del prestamista y la aprobación de las partes del cierre. Un crédito no puede exceder los costos permitidos y puede afectar las condiciones del préstamo. Esto no es una oferta de préstamo, asesoría fiscal ni una promesa de fondos.',
    ctaTitle: 'Estimemos lo que su compra podría recibir.',
    ctaBody: 'Cuéntele a Giovanni dónde y cuándo piensa comprar. Él le explicará la oferta junto con el resto de su estrategia de compra.',
    contact: 'Consultar mi elegibilidad',
    search: 'Buscar casas en Atlanta'
  }
};

export default function CashBackDeal() {
  const { language } = useLanguage();
  const c = content[language];

  return (
    <div className="gt-site gt-inner-page">
      <SEOHelmet
        title="Buyer Cash-Back Deal | Giovanni Triana"
        description="Learn how qualifying Atlanta home buyers may receive an approved closing credit or cash-back rebate through HOUSEITGOING LLC."
        path="/cash-back-deal"
      />
      <PublicHeader />
      <main>
        <section className="gt-cashback-hero">
          <div className="gt-cashback-hero-copy">
            <span className="gt-kicker">{c.kicker}</span>
            <h1>{c.title}</h1>
            <p>{c.lead}</p>
            <div className="gt-actions">
              <Link className="gt-button gt-button--gold" to="/contact-us">{c.contact} <span>→</span></Link>
              <Link className="gt-button gt-button--glass" to="/search">{c.search}</Link>
            </div>
          </div>
          <div className="gt-cashback-hero-mark" aria-hidden="true">
            <span>{c.badgeTop}</span>
            <strong>{c.badgeMain}</strong>
            <em>{c.badgeBottom}</em>
          </div>
        </section>

        <section className="gt-cashback-how gt-section">
          <header className="gt-section-head">
            <span className="gt-kicker gt-kicker--dark">{c.howKicker}</span>
            <h2>{c.howTitle}</h2>
          </header>
          <div className="gt-cashback-steps">
            {c.steps.map(([title, body], index) => (
              <article key={title}>
                <span>0{index + 1}</span>
                <h3>{title}</h3>
                <p>{body}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="gt-cashback-qualify">
          <div>
            <span className="gt-kicker">{c.qualifyKicker}</span>
            <h2>{c.qualifyTitle}</h2>
            <p>{c.qualifyBody}</p>
          </div>
          <ul>
            {c.factors.map((factor) => <li key={factor}><span>✓</span>{factor}</li>)}
          </ul>
        </section>

        <section className="gt-cashback-benefits gt-section">
          <header className="gt-section-head">
            <span className="gt-kicker gt-kicker--dark">{c.benefitKicker}</span>
            <h2>{c.benefitTitle}</h2>
          </header>
          <div className="gt-cashback-benefit-grid">
            {c.benefits.map(([title, body], index) => (
              <article key={title}>
                <span>{index + 1}</span>
                <h3>{title}</h3>
                <p>{body}</p>
              </article>
            ))}
          </div>
          <p className="gt-cashback-disclaimer">{c.disclaimer}</p>
        </section>

        <section className="gt-cashback-cta">
          <div>
            <span className="gt-kicker">HOUSEITGOING LLC</span>
            <h2>{c.ctaTitle}</h2>
            <p>{c.ctaBody}</p>
          </div>
          <div className="gt-actions">
            <Link className="gt-button gt-button--gold" to="/contact-us">{c.contact} <span>→</span></Link>
            <a className="gt-button gt-button--glass" href={`tel:${SITE_CONFIG.AGENT.PHONE}`}>{SITE_CONFIG.AGENT.PHONE}</a>
          </div>
        </section>
      </main>
    </div>
  );
}
